import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import {
  FaChevronLeft,
  FaChevronRight,
  FaCircle,
  FaComments,
  FaEllipsisV,
  FaHeart,
  FaMapMarkerAlt,
  FaTimes
} from 'react-icons/fa';

import { useTranslation } from 'react-i18next';

import '../css/UserDetails.css';


function UserModal({
  show,
  handleClose,
  user,
  onLovely,
  onChat
}) {

  const { t } = useTranslation();

  const [photoIndex, setPhotoIndex] =
    useState(0);

  const [showMoreMenu, setShowMoreMenu] =
    useState(false);

  const [showReportModal, setShowReportModal] =
    useState(false);

  const [reportReason, setReportReason] =
    useState('');

  const [reporting, setReporting] =
    useState(false);

  const [reportMessage, setReportMessage] =
    useState('');


  const photos = useMemo(() => {

    if (
      Array.isArray(user?.mediaUrls) &&
      user.mediaUrls.length > 0
    ) {
      return user.mediaUrls;
    }

    return [
      '/default-profile.jpg'
    ];

  }, [user]);


  useEffect(() => {

    if (show) {

      setPhotoIndex(0);

      setShowMoreMenu(false);

      setShowReportModal(false);

      setReportReason('');

      setReportMessage('');
    }

  }, [
    show,
    user?.id
  ]);


  useEffect(() => {

    if (!show) {
      return undefined;
    }


    const handleKeyDown =
      (event) => {

        if (event.key === 'Escape') {

          if (showReportModal) {

            if (!reporting) {
              setShowReportModal(false);
            }

            return;
          }


          if (showMoreMenu) {

            setShowMoreMenu(false);

            return;
          }


          handleClose();
        }


        if (
          event.key === 'ArrowLeft'
        ) {

          setPhotoIndex(
            previous =>
              (
                previous -
                1 +
                photos.length
              ) %
              photos.length
          );
        }


        if (
          event.key === 'ArrowRight'
        ) {

          setPhotoIndex(
            previous =>
              (
                previous +
                1
              ) %
              photos.length
          );
        }
      };


    document.addEventListener(
      'keydown',
      handleKeyDown
    );


    return () => {

      document.removeEventListener(
        'keydown',
        handleKeyDown
      );
    };

  }, [
    show,
    showMoreMenu,
    showReportModal,
    reporting,
    photos.length,
    handleClose
  ]);


  if (
    !show ||
    !user
  ) {
    return null;
  }


  const showPreviousPhoto = () => {

    setPhotoIndex(
      previous =>
        (
          previous -
          1 +
          photos.length
        ) %
        photos.length
    );
  };


  const showNextPhoto = () => {

    setPhotoIndex(
      previous =>
        (
          previous +
          1
        ) %
        photos.length
    );
  };


  const handleLikeClick =
    async () => {

      await onLovely?.();
    };


  const handleChatClick = () => {

    onChat?.();

    handleClose();
  };


  const handleSubmitReport =
    async () => {

      if (
        !user?.id ||
        !reportReason ||
        reporting
      ) {
        return;
      }


      try {

        setReporting(true);

        setReportMessage('');


        const token =
          localStorage.getItem(
            'authToken'
          );


        const authorizationHeader =
          token?.startsWith(
            'Bearer '
          )
            ? token
            : `Bearer ${token}`;


        const response =
          await fetch(
            `${process.env.REACT_APP_API_URL}/api/reports`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Authorization:
                  authorizationHeader
              },

              body:
                JSON.stringify({
                  type: 'USER',
                  reportedUserId:
                    user.id,
                  targetId: null,
                  reason:
                    reportReason
                })
            }
          );


        if (!response.ok) {

          const data =
            await response
              .json()
              .catch(
                () => ({})
              );


          throw new Error(
            data.message ||
            'Impossible d’envoyer le signalement.'
          );
        }


        setReportMessage(
          t(
            'report.success',
            'Merci. Votre signalement a été envoyé.'
          )
        );


        setReportReason('');


        window.setTimeout(
          () => {

            setShowReportModal(
              false
            );

            setReportMessage('');

          },
          1200
        );


      } catch (error) {

        console.error(
          '❌ Report error:',
          error
        );


        setReportMessage(
          error.message ||
          t(
            'report.error',
            'Impossible d’envoyer le signalement.'
          )
        );


      } finally {

        setReporting(false);
      }
    };


  return (
    <>
      <div
        className="profile-modal-backdrop"
        onMouseDown={(event) => {

          if (
            event.target ===
            event.currentTarget
          ) {
            handleClose();
          }

        }}
        role="presentation"
      >

        <section
          className="profile-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-modal-title"
          onMouseDown={(event) =>
            event.stopPropagation()
          }
        >

          {/* CLOSE */}

          <button
            type="button"
            className="profile-modal-close"
            onClick={handleClose}
            aria-label={
              t(
                'common.close',
                'Fermer'
              )
            }
          >
            <FaTimes />
          </button>


          {/* 3 DOT MENU */}

          <div
            className="profile-modal-more-wrapper"
          >

            <button
              type="button"
              className="profile-modal-more-button"
              onClick={() =>
                setShowMoreMenu(
                  previous =>
                    !previous
                )
              }
              aria-label={
                t(
                  'user.moreOptions',
                  'Plus d’options'
                )
              }
            >
              <FaEllipsisV />
            </button>


            {showMoreMenu && (

              <div
                className="profile-modal-more-menu"
              >

                <button
                  type="button"
                  className="profile-modal-report-button"
                  onClick={() => {

                    setShowMoreMenu(
                      false
                    );

                    setReportReason('');

                    setReportMessage('');

                    setShowReportModal(
                      true
                    );

                  }}
                >
                  {t(
                    'user.reportProfile',
                    'Signaler le profil'
                  )}
                </button>

              </div>

            )}

          </div>


          {/* PHOTO */}

          <div
            className="profile-modal-photo"
          >

            <img
              src={
                photos[photoIndex]
              }
              alt={`${user.name || 'Profile'} ${photoIndex + 1}`}
              draggable="false"
              onError={(event) => {

                event.currentTarget.src =
                  '/default-profile.jpg';

              }}
            />


            <div
              className="profile-modal-photo-shade"
            />


            {/* ONLINE */}

            <span
              className="profile-modal-status"
            >

              <FaCircle
                className={
                  user.online
                    ? 'is-online'
                    : 'is-offline'
                }
              />

              {user.online
                ? t(
                    'user.online',
                    'En ligne'
                  )
                : t(
                    'user.offline',
                    'Hors ligne'
                  )}

            </span>


            {/* PHOTO NAVIGATION */}

            {photos.length > 1 && (
              <>

                <button
                  type="button"
                  className="
                    profile-photo-nav
                    profile-photo-nav--prev
                  "
                  onClick={
                    showPreviousPhoto
                  }
                  aria-label={
                    t(
                      'common.previous',
                      'Précédent'
                    )
                  }
                >
                  <FaChevronLeft />
                </button>


                <button
                  type="button"
                  className="
                    profile-photo-nav
                    profile-photo-nav--next
                  "
                  onClick={
                    showNextPhoto
                  }
                  aria-label={
                    t(
                      'common.next',
                      'Suivant'
                    )
                  }
                >
                  <FaChevronRight />
                </button>


                <div
                  className="profile-photo-dots"
                >

                  {photos.map(
                    (_, index) => (

                      <button
                        key={index}
                        type="button"
                        className={
                          index ===
                          photoIndex

                            ? 'is-active'

                            : ''
                        }
                        onClick={() =>
                          setPhotoIndex(
                            index
                          )
                        }
                        aria-label={`${t(
                          'user.photo',
                          'Photo'
                        )} ${index + 1}`}
                      />

                    )
                  )}

                </div>

              </>
            )}


            {/* NAME */}

            <div
              className="profile-modal-photo-copy"
            >

              <h2
                id="profile-modal-title"
              >

                {user.name}

                <span>
                  {user.age}
                </span>

              </h2>


              <p>

                <FaMapMarkerAlt />

                {user.location ||
                  t(
                    'user.locationUnknown',
                    'Localisation non renseignée'
                  )}

              </p>

            </div>

          </div>


          {/* BODY */}

          <div
            className="profile-modal-body"
          >

            <div
              className="profile-modal-info-grid"
            >

              <ModalInfo
                label={
                  t(
                    'user.gender',
                    'Genre'
                  )
                }
                value={
                  user.gender ||
                  t(
                    'user.notSpecified',
                    'Non précisé'
                  )
                }
              />


              <ModalInfo
                label={
                  t(
                    'user.maritalStatus',
                    'Situation matrimoniale'
                  )
                }
                value={
                  user.maritalStatus ||
                  t(
                    'user.notSpecified',
                    'Non précisé'
                  )
                }
              />


              <ModalInfo
                label={
                  t(
                    'user.hasChildren',
                    'Enfants'
                  )
                }
                value={
                  user.hasChildren
                    ? t(
                        'user.yes',
                        'Oui'
                      )
                    : t(
                        'user.no',
                        'Non'
                      )
                }
              />


              <ModalInfo
                label={
                  t(
                    'user.skinTone',
                    'Teint'
                  )
                }
                value={
                  user.skinTone ||
                  t(
                    'user.notSpecified',
                    'Non précisé'
                  )
                }
              />

            </div>


            {user.bio && (

              <section
                className="profile-modal-bio"
              >

                <span>

                  {t(
                    'user.aboutMe',
                    'À propos de moi'
                  )}

                </span>


                <p>
                  {user.bio}
                </p>

              </section>

            )}

          </div>


          {/* ACTIONS */}

          <footer
            className="profile-modal-actions"
          >

            <button
              type="button"
              className="
                profile-modal-action
                profile-modal-action--like
              "
              onClick={
                handleLikeClick
              }
            >

              <FaHeart />

              {t(
                'user.like',
                'J’aime'
              )}

            </button>


            <button
              type="button"
              className="
                profile-modal-action
                profile-modal-action--message
              "
              onClick={
                handleChatClick
              }
            >

              <FaComments />

              {t(
                'user.message',
                'Message'
              )}

            </button>

          </footer>

        </section>

      </div>


      {/* REPORT MODAL */}

      {showReportModal && (

        <div
          className="report-modal-backdrop"
          onMouseDown={(event) => {

            if (
              event.target ===
              event.currentTarget &&
              !reporting
            ) {
              setShowReportModal(
                false
              );
            }

          }}
        >

          <section
            className="report-modal"
            onMouseDown={(event) =>
              event.stopPropagation()
            }
          >

            <h2>
              {t(
                'report.title',
                'Signaler ce profil'
              )}
            </h2>


            <p>
              {t(
                'report.description',
                'Pourquoi souhaitez-vous signaler ce profil ?'
              )}
            </p>


            <div
              className="report-reasons"
            >

              {[
                {
                  value:
                    'Fake profile',

                  label:
                    t(
                      'report.fakeProfile',
                      'Faux profil'
                    )
                },

                {
                  value:
                    'Harassment',

                  label:
                    t(
                      'report.harassment',
                      'Harcèlement'
                    )
                },

                {
                  value:
                    'Scam or asking for money',

                  label:
                    t(
                      'report.scam',
                      'Arnaque ou demande d’argent'
                    )
                },

                {
                  value:
                    'Inappropriate content',

                  label:
                    t(
                      'report.inappropriate',
                      'Contenu inapproprié'
                    )
                },

                {
                  value:
                    'Underage concern',

                  label:
                    t(
                      'report.underage',
                      'Personne potentiellement mineure'
                    )
                },

                {
                  value:
                    'Other',

                  label:
                    t(
                      'report.other',
                      'Autre'
                    )
                }

              ].map(
                reason => (

                  <button
                    key={
                      reason.value
                    }
                    type="button"
                    className={
                      reportReason ===
                      reason.value

                        ? 'report-reason is-selected'

                        : 'report-reason'
                    }
                    onClick={() =>
                      setReportReason(
                        reason.value
                      )
                    }
                  >

                    {reason.label}

                  </button>

                )
              )}

            </div>


            {reportMessage && (

              <p
                className="report-message"
              >
                {reportMessage}
              </p>

            )}


            <div
              className="report-modal-actions"
            >

              <button
                type="button"
                className="report-cancel"
                disabled={
                  reporting
                }
                onClick={() => {

                  setShowReportModal(
                    false
                  );

                  setReportReason('');

                  setReportMessage('');

                }}
              >

                {t(
                  'common.cancel',
                  'Annuler'
                )}

              </button>


              <button
                type="button"
                className="report-submit"
                disabled={
                  !reportReason ||
                  reporting
                }
                onClick={
                  handleSubmitReport
                }
              >

                {reporting
                  ? t(
                      'report.sending',
                      'Envoi...'
                    )
                  : t(
                      'report.submit',
                      'Signaler'
                    )}

              </button>

            </div>

          </section>

        </div>

      )}

    </>
  );
}


function ModalInfo({
  label,
  value
}) {

  return (

    <div
      className="profile-modal-info"
    >

      <span>
        {label}
      </span>

      <strong>
        {value}
      </strong>

    </div>
  );
}


export default UserModal;